// src/pages/ReservationConfirmation.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import {
  FaCalendarAlt,
  FaClock,
  FaUserMd,
  FaMoneyBillWave,
  FaUser,
  FaMapMarkerAlt
} from 'react-icons/fa';
import {
  Card,
  Button,
  Spinner,
  Container,
  Row,
  Col
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLanguage } from '../i18n/LanguageContext';
import { localizeEntity } from '../i18n/localizeData';

function ReservationConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();

  const state = location.state || {};
  const { professionalId, serviceId, date, startTime, endTime, mode } = state;
  const [emailVerified, setEmailVerified] = useState(null);

  const [professionalName, setProfessionalName] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [servicePriceCents, setServicePriceCents] = useState(0);
  const [patientInfo, setPatientInfo] = useState(null);
  const [appointmentId] = useState(crypto.randomUUID());
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (professionalId) {
      axios.get(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/prestataires/${professionalId}`)
        .then(res => setProfessionalName(`${res.data.first_name} ${res.data.last_name}`))
        .catch(console.error);
    }

    if (serviceId) {
      axios.get(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/services/${serviceId}`)
        .then(res => {
          const localizedService = localizeEntity(res.data, language);
          setServiceName(localizedService.name);
          setServicePriceCents(res.data.price_cents || 0);
        })
        .catch(console.error);
    }

    if (user?.email) {
      axios.get(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/users/email/${user.email}`)
        .then(res => {
          setPatientInfo(res.data);
          const isVerified = res.data.email_verified === true;
          setEmailVerified(isVerified);

          if (!isVerified && !emailSent) {
            axios.post(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/verify-email-request`, {
              email: res.data.email,
              locale: language
            })
              .then(() => setEmailSent(true))
              .catch(console.error);
          }
        })
        .catch(console.error);
    }
  }, [emailSent, language, professionalId, serviceId, user?.email]);

  // 🔄 Vérifie périodiquement si email_verified devient true
  useEffect(() => {
    const interval = setInterval(() => {
      if (user?.email && emailVerified === false) {
        console.log("🔁 Tentative de vérification email...");
        axios.get(`https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod/users/email/${user.email}`)
          .then(res => {
            const isVerified = res.data.email_verified === true;
            console.log("🧪 Résultat re-fetch:", res.data.email_verified);
  
            if (isVerified && emailVerified === false) {
              setPatientInfo(res.data);
              setEmailVerified(true);
              console.log('✅ Email vérifié détecté sans refresh.');
            }
          })
          .catch(err => {
            console.error('❌ Erreur durant le re-fetch de l’email :', err);
          });
      }
    }, 5000);
  
    return () => clearInterval(interval);
  }, [user?.email, emailVerified]);
  

  const handleProceedToPayment = () => {
    const appointmentData = {
      appointment_id: appointmentId,
      patient_id: patientInfo?.user_id,
      professional_id: professionalId,
      service_id: serviceId,
      start_time: `${date}T${startTime}`,
      end_time: `${date}T${endTime}`,
      mode,
      postal_code: patientInfo?.postal_code || null,
      notes: null
    };

    navigate('/paiement', {
      state: {
        appointmentData,
        servicePriceCents
      }
    });
  };

  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  const localizedDate = date ? new Date(date).toLocaleDateString(locale) : '';
  const localizedMode = mode === 'in_person' ? t('booking.inPerson') : t('booking.virtual');

  if (!professionalId || !serviceId || !date || !startTime || !endTime) {
    return <p className="text-danger text-center mt-5">{`❌ ${t('booking.missingParameters')}`}</p>;
  }

  return (
    <Container className="my-5">
      <Card className="shadow rounded p-4">
        <Card.Title className="text-primary mb-4 text-center fs-3">{t('booking.appointmentConfirmed')}</Card.Title>

        <Row>
          <Col md={6}>
            <h5 className="mb-3"><FaUserMd className="me-2" />{t('booking.professional')}</h5>
            <p>{professionalName}</p>

            <h5 className="mb-3"><FaMoneyBillWave className="me-2" />{t('booking.service')}</h5>
            <p>{serviceName}</p>

            <h5 className="mb-3"><FaCalendarAlt className="me-2" />{t('booking.appointmentDate')}</h5>
            <p>{localizedDate}</p>

            <h5 className="mb-3"><FaClock className="me-2" />{t('booking.appointmentTime')}</h5>
            <p>{startTime} - {endTime}</p>

            <h5 className="mb-3"><FaMapMarkerAlt className="me-2" />{t('booking.mode')}</h5>
            <p>{localizedMode}</p>
          </Col>

          <Col md={6}>
            <h5 className="mb-3"><FaUser className="me-2" />{t('booking.patientInfo')}</h5>
            {patientInfo ? (
              <>
                <p><strong>{t('booking.name')} :</strong> {patientInfo.first_name} {patientInfo.last_name}</p>
                <p><strong>{t('booking.email')} :</strong> {patientInfo.email}</p>
                <p><strong>{t('booking.phone')} :</strong> {patientInfo.phone || t('common.noneProvided')}</p>
                <p><strong>{t('booking.address')} :</strong> {patientInfo.address || t('common.noneProvidedFeminine')}</p>
                <p><strong>{t('booking.postalCode')} :</strong> {patientInfo.postal_code || t('common.noneProvided')}</p>
                <p><strong>{t('booking.birthDate')} :</strong> {patientInfo.birth_date || t('common.noneProvidedFeminine')}</p>
              </>
            ) : (
              <Spinner animation="border" variant="primary" />
            )}

            <hr />
            <h5 className="mb-3"><FaMoneyBillWave className="me-2" />{t('booking.amountToPay')}</h5>
            <p className="fs-4 fw-bold">{(servicePriceCents / 100).toFixed(2)} $</p>

            {emailVerified === false && (
              <>
                <div className="alert alert-warning">
                  ⚠️ {t('booking.emailNotVerified')}<br />
                  {t('booking.confirmationSent')} <strong>{patientInfo?.email}</strong>.
                </div>
                <p className="text-success">📩 {t('booking.checkInbox')}</p>
              </>
            )}

            {emailVerified === true && (
              <div className="alert alert-success">✅ {t('booking.emailVerified')}</div>
            )}

            <Button
              variant="primary"
              size="lg"
              className="mt-3 w-100"
              onClick={handleProceedToPayment}
              disabled={!patientInfo || emailVerified !== true}
            >
              {t('booking.proceedToPayment')}
            </Button>
          </Col>
        </Row>
      </Card>
    </Container>
  );
}

export default ReservationConfirmation;
